import { getOwner } from "@ember/application"
import type Service from "@ember/service"
import { createContext, useContext } from "react"

/**
 * Holds the Ember object (the react-render modifier instance) used to look up services. React
 * components rendered via the react-render modifier are wrapped in an EmberServiceProvider, so
 * they can reach Ember services with {@link useEmberService}.
 */
export const EmberServiceContext = createContext<object | null>(null)

/** Look up an Ember service by name from inside a React component rendered via react-render. */
export function useEmberService<S extends Service>(name: string): S {
  const modifier = useContext(EmberServiceContext)
  if (!modifier) {
    throw new Error(
      `useEmberService("${name}") must be used inside an EmberServiceProvider (i.e. rendered via the react-render modifier).`,
    )
  }

  const owner = getOwner(modifier)
  if (!owner) {
    throw new Error(`Could not find the Ember owner while looking up service "${name}".`)
  }

  const service = owner.lookup(`service:${name}`) as S | undefined
  if (!service) {
    throw new Error(`Could not find Ember service "${name}".`)
  }

  return service
}
